// apps/lk/src/lib/use-dashboard.ts
// Единый хук для загрузки дашборда с кешированием.
// Логика:
//   1. Свежий кеш (< 5 мин) → мгновенный рендер, НЕТ запроса
//   2. Устаревший кеш → мгновенный рендер старых данных + 1 фоновый запрос
//   3. Нет кеша → skeleton + запрос
// Все страницы используют этот хук → данные грузятся один раз.

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { getCachedDashboard, getStaleDashboard, needsRefresh, setCachedDashboard } from '@/lib/dashboard-cache';

export function useDashboard(userId: string | undefined) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) return;

    // 1) Свежий кеш — рендерим сразу, запроса НЕТ
    const fresh = getCachedDashboard(userId);
    if (fresh) {
      setData(fresh);
      setLoading(false);
      return; // ← ключевое: выходим, запроса к серверу нет
    }

    // 2) Устаревший кеш — рендерим старые данные сразу
    const stale = getStaleDashboard(userId);
    if (stale) {
      setData(stale);
      setLoading(false);
    }

    // 3) Нужен ли фоновый запрос?
    if (!needsRefresh(userId)) return;

    api.dashboard()
      .then((raw: any) => {
        const d = raw?.data ?? raw;
        if (d?.bigFive !== undefined) {
          setData(d);
          setCachedDashboard(userId, d);
          setError('');
        } else if (!stale) {
          setError('Не удалось загрузить данные');
        }
      })
      .catch(() => { if (!stale) setError('Не удалось загрузить данные'); })
      .finally(() => { if (!stale) setLoading(false); });
  }, [userId]);

  return { data, loading, error };
}
